import { Component, computed, inject } from '@angular/core';
import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { getEmployeeEntities } from '@app/reducers/orm/employee/employee.selectors';
import { ScheduleModel } from '@app/reducers/orm/schedule/schedule.model';
import { getEnhancedScheduleEntities } from '@app/reducers/orm/schedule/schedule.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { AvatarComponent } from '@sb/ui/avatar';
import { BadgeComponent } from '@sb/ui/badge';
import { ButtonComponent } from '@sb/ui/button';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';

import { FindCoverShiftRowComponent } from '../shift-row/find-cover-shift-row.component';

/**
 * Closes the multi-shift run: what got covered, what is still open, and a way back into
 * the ones that are. `assignedEmployeeIds` is keyed by occurrence id.
 */
export interface FindCoverSummaryData {
  occurrenceIds: string[];
  assignedEmployeeIds: Record<string, string>;
}

interface SummaryRow {
  shift: ScheduleModel;
  coveredBy: EmployeeModel | undefined;
}

@Component({
  templateUrl: './find-cover-summary.component.html',
  imports: [
    SbDialogModule,
    TranslatePipe,
    ButtonComponent,
    AvatarComponent,
    BadgeComponent,
    FindCoverShiftRowComponent,
  ],
})
export class FindCoverSummaryComponent {
  #data = inject<FindCoverSummaryData>(DIALOG_DATA);
  #dialogRef = inject<DialogRef<string | undefined>>(DialogRef);
  #store = inject<Store<AppState>>(Store);

  #scheduleEntities = this.#store.selectSignal(getEnhancedScheduleEntities);
  #employeeEntities = this.#store.selectSignal(getEmployeeEntities);

  rows = computed<SummaryRow[]>(() => {
    const scheduleEntities = this.#scheduleEntities();
    const employeeEntities = this.#employeeEntities();

    return this.#data.occurrenceIds
      .map((occurrenceId) => scheduleEntities[occurrenceId])
      .filter((shift): shift is ScheduleModel => !!shift)
      .map((shift) => {
        const employeeId = this.#data.assignedEmployeeIds[shift.occurrence_id];

        return { shift, coveredBy: employeeId ? employeeEntities[employeeId] : undefined };
      });
  });

  coveredCount = computed(() => this.rows().filter((row) => !!row.coveredBy).length);

  done(): void {
    this.#dialogRef.close(undefined);
  }

  findCover(occurrenceId: string): void {
    this.#dialogRef.close(occurrenceId);
  }
}
